import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import moment from 'moment';
import Button from '../Button/Button';
import Checkbox from '../Checkbox/Checkbox';
import DateInput from '../DateInput/DateInput';
import Group from '../Group/Group';
import Input from '../Input/Input';
import Languagebar from '../Languagebar/Languagebar';
import Menu from '../Menu/Menu';
import Select from '../Select/Select';
import SelectList from '../SelectList/SelectList';
import TagInput from '../TagInput/TagInput';
import TextArea from '../TextArea/TextArea';
import createFakeEvent from '../../lib/createFakeEvent';
import validateMetadata from '../../lib/validateMetadata';
import Gravity from '../../constants/Gravity';
import { isNil } from '../../lib/utils';

const ALL = 'default';
const FALLBACK_VALUES = {
  BOOLEAN: false,
  DATE: moment(),
  NUMBER: 0,
  PASSWORD: '',
  STRING: '',
};
const TAG_SEPARATOR = / ?, ?/;
const TAG_DELIMITER = ', ';
const TAG_PREFIX = '#';

/**
 * `Metadata` is component for editing metadata.
 *
 * The fields will be generated based on the *metadata* and *metadataDefinitions* props. All fields in your metadata that
 * have values will be shown and be editable if the metadata definition permits it.
 *
 * Fields configured as `required` and `defaultVisible` will always be shown unless the *ignoreRequired* or
 * *ignoreDefaultVisible* props are set. Optional fields can be added and removed by the user.
 *
 * The *metadata* prop contains the values that will be displayed with localized values stored for each locale id. For
 * example, the following metadata object
 * ````
 * {
 *   default: {
 *     title: 'This is the default title',
 *     text: 'This value is displayed for all languages'
 *   },
 *   en_US: {
 *     someOtherKey: 'This is the English value'
 *   },
 *   no_NB: {
 *     title: 'This is the Norwegian title'
 *   }
 * }
 * ````
 * will display the Norwegian title when the 'Norwegian' locale tab is selected and the title property is multilingual. If
 * the 'English' tab is selected, the default title will be displayed, because the en_US block does not contain a
 * multilingual value.
 *
 * The component supports multilingual fields and if *locales* is provided, the component will allow new localized values to
 * be added and removed from the metadata.
 *
 * The component is tuned for metadata editing out of the box. However, the component may also be used as a search filter if
 * the *filterable* prop is set to `true`. This will **only** show fields that have `filterable=true` in the metadata
 * definition regardless of the field is `required` or `defaultVisible`.
 *
 * `Metadata` uses a {@link Languagebar} to display its locale selection. The language bar will not be shown when there is
 * only one locale.
 *
 * See [Metadata Data Model](https://vimond-ng.atlassian.net/wiki/display/VPS/Metadata+Data+Model) for more on the data
 * types available.
 *
 * @property {string} addFieldText The text to display for the **Add Field** button. Defaults to "Add Field".
 * @property {string} addLanguageText The text of the **Add Language** button. Defaults to "+ Add Language".
 * @property {boolean} allowWrite Some metadata fields may allow you to add data elements to the predefined set - e.g.
 * adding new tags that can be shared with all users. This information is usually defined in a permission system and setting
 * this prop to `true` will allow the user to add new data. Defaults to `false`.
 * @property {boolean} autoFocus If set to `true`, the first input or text area field will receive the input focus. Defaults
 * to `true`.
 * @property {string} className The class for this component. Will be appended "core-metadata".
 * @property {object} defaultValues Optional application specific default values not set by *metadataDefinitions*.
 * @property {boolean} disabled If this is set to `true`, all fields and buttons will be disabled no matter what the
 * metadata definitions dictate. Defaults to `false` (all fields are enabled and metadata definitions are taken into
 * consideration).
 * @property {boolean} filterable If set to `true`, only fields that have metadata definition filterable equal to `true` will
 * be displayed. Defaults to `false` meaning that all fields that have value will be shown regardless of filterable state.
 * @property {boolean} ignoreDefaultVisible If set to `true`, the fields that are `defaultVisible=true` in
 * *metadataDefinitions* will not be shown by default. Defaults to `false`.
 * @property {boolean} ignoreRequired If set to `true`, the required field in each metadata definition entry will be ignored
 * and can be removed by the user. Defaults to `false`.
 * @property {array} locales An array of locales as returned by the Vimond API. Multilingual support will only be available
 * when this prop is defined. Defaults to [].
 * @property {number} maxLength The maximum number of characters that can be entered into this input. Defaults to unlimited
 * length.
 * @property {object} metadata The metadata values. Any defined values will make the associated field show in the filter
 * dialog. Multilingual fields will display the localized value if present in the *metadata* object. Defaults to {}.
 * @property {object} metadataDefinitions The metadata definitions. The component will use this data structure to determine
 * the data type, default value, etc.
 * @property {string} multilingualText The text to display for all multilingual fields. Defaults to `(applies to all
 * languages)`.
 * @property {function} onAddLocale A callback that is called when the user adds a new locale to the tabs. This prop will
 * only have an effect when *locales* is defined.
 * @property {function} onBlur A callback that will be called whenever a field in the `Metadata` loses input focus.
 * @property {function} onChange A callback that is called when the user changes metadata values. The callback will be
 * passed 3 parameters, the event (where `event.target.value` is a copy of the entire metadata object passed to it with the
 * edited field is updated to hold the new value), `key` (the metadata definition name of the edited field) and `value`
 * (new value).
 * @property {function} onChangeLocale A callback that is called whenever the user has selected a language.
 * @property {function} onFocus A callback that is called whenever a field in the `Metadata` gains input focus.
 * @property {function} onRemoveLocale A callback that is called whenever the user has requested the removal of a locale. The
 * callback will be passed 3 parameters, the event (where `event.target.value` is the locale requested to be removed),
 * `nextMetadata` (a copy of the metadata passed to this component, but with all multilingual values for the deleted locale
 * deleted) and `nextSelected` (the locale to select next should you update the props with the values passed to this
 * callback. You may use this callback to display confirmation dialogs etc. The locale will be deleted if you set the
 * `nextMetadata` and the `nextSelected` values as props.
 * @property {string} selected The ID of the locale that `Metadata` will show the fields for. Defaults to "default".
 * @property {boolean} showAddField Displays the **Add Field** field allowing users to add more fields to the list. Defaults
 * to `true`.
 * @property {boolean} showAddLanguage If set to `true`, the language bar will display a **+ Add Language** button that the
 * user can click to add more languages. Defaults to `true`.
 * @property {boolean} showCharacterCount If set to true, it will show the character count on text input field types. Defaults to `false`.
 * @property {boolean} showLanguageBar Displays the language bar if the locales are also set. Defaults to `true`.
 * @property {boolean} showRemove Displays a remove button for each tab allowing the user to remove multilingual values from
 * the metadata. Defaults to `true`.
 * @property {object} suggestions An object of suggestions that are lazily fetched as the user types in values. This is used
 * for fields of type "AUTOCOMPLETE". The keys in *suggestions* should be the `name` of the metadata definition entry.
 * See how [VCC handles the "AUTOCOMPLETE" field type](https://github.com/vimond/vimond-cms/blob/master/src/client/common/components/Metadata.jsx).
 * @property {string} title The title of this component. Defaults to no title.
 *
 * @see Languagebar
 */
export default class Metadata extends Component {

  static displayName = 'Metadata';

  static propTypes = {
    addFieldText: PropTypes.string,
    addLanguageText: PropTypes.string,
    allowWrite: PropTypes.bool,
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    defaultValues: PropTypes.object,
    disabled: PropTypes.bool,
    filterable: PropTypes.bool,
    ignoreDefaultVisible: PropTypes.bool,
    ignoreRequired: PropTypes.bool,
    locales: PropTypes.array,
    maxLength: PropTypes.number,
    metadata: PropTypes.object,
    metadataDefinitions: PropTypes.object,
    multilingualText: PropTypes.string,
    onAddLocale: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onChangeLocale: PropTypes.func,
    onFocus: PropTypes.func,
    onRemoveLocale: PropTypes.func,
    selected: PropTypes.string,
    showAddField: PropTypes.bool,
    showAddLanguage: PropTypes.bool,
    showCharacterCount: PropTypes.bool,
    showLanguageBar: PropTypes.bool,
    showRemove: PropTypes.bool,
    suggestions: PropTypes.object,
    title: PropTypes.string,
  };

  static defaultProps = {
    addFieldText: 'Add field',
    autoFocus: true,
    defaultValues: {},
    filterable: false,
    locales: [],
    metadata: {},
    multilingualText: '(applies to all languages)',
    selected: ALL,
    showAddField: true,
    showAddLanguage: true,
    showLanguageBar: true,
    showRemove: true,
    suggestions: {},
  };

  static getVisibleLocales(metadata, locales) {
    const visible = Object.keys(metadata).filter(localeId => localeId !== ALL);
    let defLocale;

    if (visible.length === 0 && (defLocale = locales.find(locale => locale.default))) {
      return [defLocale];
    }
    return visible
      .map(localeId => locales.find(locale => localeId === locale.localeId))
      .filter(locale => locale)
      .sort((a, b) => a.language.localeCompare(b.language));
  }

  static getSanitizedTags(tagString) {
    return tagString ? tagString.replace(new RegExp(TAG_PREFIX,'g'), '').split(TAG_SEPARATOR).map(tag => tag.trim()).filter(tag => tag.length) : [];
  }

  state = {
    fieldsFilter: '',
  };

  onAddLocale = e => {
    const { metadata, metadataDefinitions, onAddLocale, onChange, onChangeLocale, ignoreRequired, ignoreDefaultVisible } = this.props;
    const locale = e.target.value;

    if (onChange) {
      const data = { ...metadata };
      const localeId = locale.localeId;
      const scope = data[localeId] = {};

      Object.keys(metadataDefinitions)
        .filter(type => (
          (metadataDefinitions[type].required && !ignoreRequired) ||
          (metadataDefinitions[type].defaultVisible && !ignoreDefaultVisible)
        ) && metadataDefinitions[type].multilingual)
        .forEach(name => {
          const def = metadataDefinitions[name];

          scope[name] = def.defaultValue !== undefined ? def.defaultValue : FALLBACK_VALUES[def.dataType];
        });
      onChange(createFakeEvent(data));
    }
    if (onChangeLocale) {
      onChangeLocale(createFakeEvent(locale));
    }
    if (onAddLocale) {
      onAddLocale(createFakeEvent(locale));
    }
  };

  onAddField = e => {
    const { metadata, onChange, selected } = this.props;

    if (onChange) {
      const def = e.target.value;
      const localeId = def.multilingual ? selected : ALL;
      const data = { ...metadata };

      if (!data[localeId]) {
        data[localeId] = {};
      }
      data[localeId][def.name] = def.defaultValue !== undefined ? def.defaultValue : FALLBACK_VALUES[def.dataType];
      this.setState({ addFieldVisible: false, fieldsFilter: '' });
      onChange(createFakeEvent(data), def.name, data[localeId][def.name], def);
    }
  };

  onAddFieldClicked = () => this.setState({ addFieldVisible: true });

  onAddFieldClosed = () => this.setState({ addFieldVisible: false });

  getDefaultMetadata = () => {
    const { metadataDefinitions, ignoreRequired, ignoreDefaultVisible } = this.props;
    const sharedDefaultMetadata = {};
    const localeDefaultMetadata = {};
    Object.keys(metadataDefinitions).forEach(key => {
      const definition = metadataDefinitions[key];
      if (
        !isNil(definition.defaultValue) &&
        (
          (definition.required && !ignoreRequired) ||
          (definition.defaultVisible && !ignoreDefaultVisible)
        )
      ) {
        const isBoolean = definition.dataType === 'BOOLEAN' && typeof definition.defaultValue === 'boolean';
        const value = isBoolean ? definition.defaultValue.toString() : definition.defaultValue;
        if (definition.multilingual) {
          localeDefaultMetadata[key] = value;
        } else {
          sharedDefaultMetadata[key] = value;
        }
      }
    });
    return {sharedDefaultMetadata, localeDefaultMetadata};
  }

  onChange = (key, e, autoComplete) => {
    const { metadata, metadataDefinitions, onChange, selected } = this.props;

    if (onChange) {
      const data = { ...metadata };
      const def = metadataDefinitions[key];
      const localeId = def.multilingual ? selected : ALL;
      let value;

      const selectedLocaleMetadataContainsValues = data[localeId] && Object.keys(data[localeId]).length > 0;
      const sharedMetadataContainsValues = data[ALL] && Object.keys(data[ALL]).length > 0;
      if (!selectedLocaleMetadataContainsValues || !sharedMetadataContainsValues) {
        const {sharedDefaultMetadata, localeDefaultMetadata} = this.getDefaultMetadata();
        if (!selectedLocaleMetadataContainsValues) {
          if (localeId === ALL) {
            data[localeId] = {...localeDefaultMetadata, ...sharedDefaultMetadata};
          } else {
            data[localeId] = localeDefaultMetadata;
          }
        }
        if (!sharedMetadataContainsValues && localeId !== ALL) {
          data[ALL] = sharedDefaultMetadata;
        }
      }
      if (def.fieldType === 'SELECT_MULTIPLE_DYNAMIC' && !autoComplete) {
        // We don't want to process changes from the tag input triggered from key presses.
        // Only Enter presses constituting dropdown selections should be processed.
        if (!autoComplete) {
          return;
        }
      } else if (def.fieldType === 'SELECT_MULTIPLE' || def.fieldType === 'SELECT_MULTIPLE_DYNAMIC') {
        value = TagInput.getSelectedTags(e.target.value, TAG_DELIMITER, TAG_PREFIX).join(TAG_DELIMITER);
      } else if (def.fieldType === 'SELECT') {
        value = typeof e.target.value === 'object' ? e.target.value.value : e.target.value; // HACK Select returns object even though an array string is sent in
      } else if (def.dataType === 'DATE' || def.fieldType === 'DATE' ) { // TODO DateInput returns the value directly. should return an event instead
        value = e;
      } else if (def.dataType === 'BOOLEAN') {
        value = (!data[localeId][key] || data[localeId][key] === 'false') ? 'true' : 'false'; // HACK yuck, booleans are stored as strings in the backend
      } else if (def.dataType === 'NUMBER') {
        value = parseFloat(e.target.value);
      } else {
        value = e.target.value;
      }
      if (data[localeId][key] !== value) {
        data[localeId][key] = value;
        this.setState({
          validationStatus: validateMetadata(data, metadataDefinitions),
        });
        onChange(createFakeEvent(data), key, value, def);
      }
    }
  };

  onFilterFields = e => {
    this.setState({
      fieldsFilter: e.target.value.toLowerCase(),
    });
  };

  onSelectMultipleDynamicEnter = (key, e, autoComplete, fieldValue) => {
    const { allowWrite, metadataDefinitions } = this.props;

    if (e.key === 'Enter' && allowWrite && metadataDefinitions && fieldValue.length) {
      const def = metadataDefinitions[key];

      const fieldValueTags = Metadata.getSanitizedTags(fieldValue).filter((tag, index, tags) => tags.indexOf(tag) === index);

      // HACK since we do not have an API for adding new tags we simply update the metadata definitions directly
      if (def.options && def.options.length) {
        const optionsList = Metadata.getSanitizedTags(def.options);
        const newOptionsList = fieldValueTags.filter(tag => !optionsList.includes(tag));
        if (newOptionsList.length) {
          def.options = [def.options].concat(newOptionsList).join(TAG_DELIMITER);
        }
      } else {
        def.options = fieldValueTags.join(TAG_DELIMITER);
      }

      const data = this.getScope(key);
      const dataTags = Metadata.getSanitizedTags(data[key]);
      const tagCount = dataTags.length;
      fieldValueTags.forEach(fieldTag => {
        if (dataTags.indexOf(fieldTag) === -1) {
          dataTags.push(fieldTag);
        }
      });

      if (dataTags.length !== tagCount) {
        const tags = dataTags.map(tag => `${TAG_PREFIX}${tag}`).join(TAG_DELIMITER);
        this.onChange(key, createFakeEvent(tags, e), true);
      }
    }
  };

  onRemoveField = e => {
    const { metadataDefinitions, onChange, selected } = this.props;
    const field = e.target.getAttribute('data-field');
    const metadata = { ...this.props.metadata };
    const localeId = metadataDefinitions[field].multilingual ? selected : ALL;

    if (onChange) {
      setTimeout(() => { // HACK: do delayed invocation in case the component is inside a popup menu
        delete metadata[localeId][field];
        onChange(createFakeEvent(metadata));
      }, 0);
    }
  };

  onRemoveLocale = (e, index, selected) => {
    if (this.props.onRemoveLocale) {
      const metadata = { ...this.props.metadata };
      const visible = Metadata.getVisibleLocales(metadata, this.props.locales);
      const locale = visible[index];

      visible.splice(index, 1);
      delete metadata[locale.localeId];
      this.props.onRemoveLocale(createFakeEvent(locale), metadata, selected);
    }
  };

  getScope = (key) => {
    const { metadata, metadataDefinitions, selected } = this.props;
    const def = metadataDefinitions[key];
    const localeId = def.multilingual ? selected : ALL;

    if (!metadata[localeId]) {
      metadata[localeId] = {};
    }
    return metadata[localeId];
  };

  createMultilingualIndicator = () => {
    const { metadata, locales, multilingualText, showLanguageBar } = this.props;
    const visible = Metadata.getVisibleLocales(metadata, locales);

    return showLanguageBar && visible.length > 1 && <span className="multilingual">{multilingualText}</span>;
  };

  createField = (name, value, index, errorMessage) => {
    const { autoFocus, defaultValues, locale, dateFormat, use24HourClock, filterable, ignoreRequired, maxLength, metadataDefinitions, onBlur, onFocus, showCharacterCount, suggestions } = this.props;
    const def = metadataDefinitions[name];
    const fieldType = def.fieldType;
    const onChange = this.onChange.bind(this, name);
    const required = !ignoreRequired && def.required;
    const disabled = this.props.disabled ? true : def.isProtected && !filterable;
    const props = { id: name, className: 'field', disabled, required, errorMessage };

    value = value || defaultValues[name]; // eslint-disable-line no-param-reassign
    try {
      if (!fieldType || fieldType === 'TEXT' || fieldType === 'PASSWORD') {
        let type = def.fieldType;

        if (def.dataType === 'NUMBER') {
          type = def.dataType;
          value = !isNaN(value) ? value : undefined; // eslint-disable-line no-param-reassign
        }
        return (
          <Input {...props} onBlur={onBlur} onFocus={onFocus} onChange={onChange} value={value} type={type} title={def.description}
            autoFocus={autoFocus && index === 0} maxLength={maxLength} showCharacterCount={showCharacterCount && type === 'TEXT'}/>
        );
      } else if (fieldType === 'CHECKBOX') {
        return (
          <Checkbox {...props} onChange={onChange} checked={value === true || value === 'true'} label={def.description}>
            {!def.multilingual && this.createMultilingualIndicator()}
          </Checkbox>
        );
      } else if (fieldType === 'TEXTAREA') {
        return (
          <TextArea {...props} onBlur={onBlur} onFocus={onFocus} onChange={onChange} value={value} title={def.description}
            maxLength={maxLength} showCharacterCount autoFocus={autoFocus && index === 0}/>
        );
      } else if (fieldType === 'SELECT') {
        const options = def && def.options && def.options.trim().split(/ ?, ?/);

        return <Select {...props} onBlur={onBlur} onFocus={onFocus} onChange={onChange} options={options} value={value}/>;
      } else if (fieldType === 'DATE') {
        return <DateInput {...props} locale={locale} dateFormat={dateFormat} use24HourClock={use24HourClock} onBlur={onBlur} onFocus={onFocus} onChange={onChange} selected={value && moment(value)}/>;
      } else if (fieldType === 'SELECT_MULTIPLE' || fieldType === 'SELECT_MULTIPLE_DYNAMIC') {
        const options = def.options && Metadata.getSanitizedTags(def.options);
        if (Array.isArray(value)) {
          value = value.join(TAG_DELIMITER); // eslint-disable-line no-param-reassign
        }
        const tmp = value ? Metadata.getSanitizedTags(value).map(tag => `${TAG_PREFIX}${tag}`).join(TAG_DELIMITER) : '';
        const onKeyDown = fieldType === 'SELECT_MULTIPLE_DYNAMIC' ? this.onSelectMultipleDynamicEnter.bind(this, name) : undefined;

        return (<TagInput {...props} options={options} onChange={onChange} value={tmp} delimiter={TAG_DELIMITER} title={def.description}
          onKeyDown={onKeyDown} showCharacterCount={showCharacterCount} showOptionsOnFocus addable={fieldType === 'SELECT_MULTIPLE_DYNAMIC'} />);
      } else if (fieldType === 'AUTOCOMPLETE') {
        return <Input {...props} value={value} onChange={onChange} options={suggestions && suggestions[name]} showOptionsOnFocus title={def.description}/>;
      }
    } catch (e) {
      console.error(`Unable to create field for ${name} (value: '${JSON.stringify(value)}', type '${def.dataType}')\n`, e);
    }
    return <span className="unsupported">{value}</span>;
  };

  render() {
    const { addFieldText, addLanguageText, className, disabled, filterable, ignoreDefaultVisible, ignoreRequired, locales,
      metadata, metadataDefinitions, onChangeLocale, showAddField, showAddLanguage, showLanguageBar, showRemove,
      title } = this.props;
    const { fieldsFilter, validationStatus } = this.state;
    const multilingual = this.createMultilingualIndicator();
    let selected = this.props.selected;
    let bar;
    let fields;
    let addField;

    if (metadata && metadataDefinitions) {
      const hasData = Object.keys(metadata).filter(key => key !== ALL);

      if (hasData && hasData.length && hasData.indexOf(selected) === -1) {
        selected = hasData.sort((a, b) => a.localeCompare(b))[0];
      }
      if (locales.length > 1 && showLanguageBar) {
        bar = (
          <Languagebar locales={locales} visible={Metadata.getVisibleLocales(metadata, locales)} onAdd={this.onAddLocale}
            onRemove={this.onRemoveLocale} addLanguageText={addLanguageText} showAddLanguage={showAddLanguage}
            onChange={onChangeLocale} selected={selected} disabled={disabled}/>
        );
      }
      const requiredAndDefaultVisible = Object.keys(metadataDefinitions)
        .filter(name => (metadataDefinitions[name].required && !ignoreRequired) || (metadataDefinitions[name].defaultVisible
        && !ignoreDefaultVisible))
        .map(name => metadataDefinitions[name])
        .reduce((obj, definition) => {
          obj[definition.name] = definition.defaultValue || FALLBACK_VALUES[definition.dataType];
          return obj;
        }, {});
      const values = { ...requiredAndDefaultVisible, ...(metadata[ALL] || {}), ...metadata[selected] };

      fields = Object.keys(values)
        .filter(key => {
          if (filterable && metadataDefinitions[key]) {
            return metadataDefinitions[key].filterable;
          }
          return !metadataDefinitions[key] || !metadataDefinitions[key].hidden;
        })
        .sort((a, b) => (metadataDefinitions[a] && metadataDefinitions[b] ? metadataDefinitions[a].sortOrder - metadataDefinitions[b].sortOrder : 0));
      fields = fields.map((name, index) => {
        const definition = metadataDefinitions[name];

        if (definition) {
          const errorMessage = validationStatus && (validationStatus[selected] || validationStatus.default || {})[name];
          const cls = cx(
            'property',
            `type-${definition.fieldType ? definition.fieldType.toLowerCase() : 'text'}`,
            `name-${name.toLowerCase()}`,
            {
              'no-space': index < fields.length - 1 && definition.dataType === 'BOOLEAN' && metadataDefinitions[fields[index + 1]].dataType === 'BOOLEAN',
            });
          let remove;

          if (showRemove && !definition.defaultVisible && (!definition.required || ignoreRequired) && (filterable || !definition.isProtected)) {
            remove = <Button iconName="delete" className="remove" onClick={this.onRemoveField} data-field={name} disabled={disabled}/>;
          }
          return (
            <div key={index} className={cls}>
              {definition.dataType !== 'BOOLEAN' && <label htmlFor={name} title={definition.description}>{definition.title}{!definition.multilingual && multilingual}</label>}
              <div className="property-field-wrapper">
                {this.createField(name, values[name], index, errorMessage)}
                {remove}
              </div>
            </div>
          );
        }
        console.error(`Metadata definition missing for ${name}`);
        return undefined;
      });
    }
    if (showAddField && metadataDefinitions) {
      const remainingFields = Object.keys(metadataDefinitions)
        .filter(name => {
          const definition = metadataDefinitions[name];
          const show = (!definition.defaultVisible || ignoreDefaultVisible) && !definition.hidden &&
            definition.title.toLowerCase().indexOf(fieldsFilter) > -1 &&
            (!metadata[selected] || (metadata[selected][name] === undefined &&
            (metadata && (metadata[ALL] === undefined || (metadata[ALL] && metadata[ALL][name] === undefined)))));

          return filterable ? definition.filterable && show : show && !definition.isProtected;
        })
        .map(name => metadataDefinitions[name])
        .sort((a, b) => a.title.localeCompare(b.title));

      addField = (
        <Menu className="add-field" title={addFieldText} gravity={Gravity.SOUTH} arrowSize={0} visible={this.state.addFieldVisible}
          customContent showOpenCloseIcon disabled={disabled} onClick={this.onAddFieldClicked} onClose={this.onAddFieldClosed}>
          <SelectList options={remainingFields} labelKey="title" valueKey="name" showSearch={true}
            onSearch={this.onFilterFields} searchValue={fieldsFilter} onChange={this.onAddField} autoFocus/>
        </Menu>
      );
    }
    return (
      <Group className={cx('core-metadata', className)} title={title}>
        {bar}
        {fields}
        {addField}
      </Group>
    );
  }

}
